import React from "react";
import { useContext, useState } from "react";
import { toInt } from "../_utils/global";
import { useCustomScrollObjectSelector } from "../_store/customScrollSelectorHook";
import { verticalBarStateSelector } from "./verticalBarDomain";
import { isAllValuesDefined } from "../_utils/predicates";
import { getThumbSize } from "../_shared/getThumbSize";
import { PerfectScrollContext } from "../scroll/perfectScrollContext";
import { useVerticalRailClickHandler } from "../_handlers/clickRail";
import { useDragVerticalThumbHandler } from "../_handlers/dragThumb";
import { useMemoEx } from "../_utils/hooks";
import {
    defaultThumbParams,
    IThumbParams,
    TThumbApi,
} from "../_shared/IThumbParams";
import { classNames } from "../_utils/classNames";

const v = "vertical";
const localNames = {
    rail: bl => classNames.element.rail(bl, v),
    thumb: bl => classNames.element.thumb(bl, v),
    scrolling: bl => classNames.state.scrolling(bl, v),
};

export function VerticalBarElement({
    style = {},
    thumbStyle = {},
    block = "ngd-perfect-scrollbar",
}) {
    const [rail, setRail] = useState<HTMLDivElement>();

    const customScrollContext = useContext(PerfectScrollContext);
    const s = useCustomScrollObjectSelector(
        verticalBarStateSelector,
        customScrollContext
    );

    const [c, getC] = useMemoEx(() => calculateThumbParams(rail, s), [s, rail]);
    const args: TThumbApi = [rail, getC, customScrollContext.store];

    const tStyle = {
        ...thumbStyle,
        top: c.thumbOffset + "px",
        height: c.thumbMeasure + "px",
    };

    const onRailClick = useVerticalRailClickHandler(...args);
    const { isDragging, ...thumbEvents } = useDragVerticalThumbHandler(...args);

    let className = localNames.rail(block);
    if (isDragging) className += " " + localNames.scrolling(block);

    return !s.scrollbarYActive ? null : (
        <div
            ref={setRail}
            style={style}
            onMouseDown={onRailClick}
            className={className}>
            <div
                style={tStyle}
                {...thumbEvents}
                className={localNames.thumb(block)}
            />
        </div>
    );
}

function calculateThumbParams(
    rail: HTMLDivElement,
    i: ReturnType<typeof verticalBarStateSelector>
): IThumbParams {
    if (!rail || !i.scrollbarYActive) return defaultThumbParams;

    /*
i.railYHeight = i.containerHeight - i.railYMarginHeight;
i.railYRatio = i.containerHeight / i.railYHeight;
i.scrollbarYHeight = getThumbSize(
i,
toInt((i.railYHeight * i.containerHeight) / i.contentHeight)
);
i.scrollbarYTop = toInt(
(roundedScrollTop * (i.railYHeight - i.scrollbarYHeight)) /
(i.contentHeight - i.containerHeight)
);
*/
    if (
        !isAllValuesDefined(
            i.containerHeight,
            i.contentHeight,
            i.roundedScrollTop
        )
    )
        return defaultThumbParams;

    const railMeasure = Math.round(rail.getBoundingClientRect().height);

    const thumbMeasure = getThumbSize(
        i.minScrollbarLength,
        i.maxScrollbarLength,
        toInt((railMeasure * i.containerHeight) / i.contentHeight)
    );

    const thumbOffset = toInt(
        (i.roundedScrollTop * (railMeasure - thumbMeasure)) /
            (i.contentHeight - i.containerHeight)
    );

    return { thumbOffset, thumbMeasure, railMeasure };
}
